import { useEffect, useRef, memo } from 'react'

interface Props {
  ticker: string
  interval?: string      // Polywally TF: '1m','5m','15m','1H','4H','1D','1W','1Y'
  height?: number
  // AI levels — shown as a compact overlay bar below the chart
  entry?: number | null
  stopLoss?: number | null
  target1?: number | null
  target2?: number | null
  direction?: string     // 'LONG' | 'SHORT' | 'WAIT'
}

// Map Polywally timeframes to TradingView intervals
const TV_INTERVAL: Record<string, string> = {
  '1m': '1', '1': '1',
  '5m': '5', '5': '5',
  '15m': '15', '15': '15',
  '1H': '60', '60': '60',
  '4H': '240', '240': '240',
  '1D': 'D', 'D': 'D',
  '1W': 'W', 'W': 'W',
  '1Y': 'M', 'M': 'M',
  '3M': '3M',
}

function TradingViewChart({ ticker, interval = '1D', height = 420, entry, stopLoss, target1, target2, direction }: Props) {
  const containerRef = useRef<HTMLDivElement>(null)
  const widgetRef = useRef<any>(null)

  // Normalize ticker for TradingView (remove X: prefix for crypto, etc.)
  const tvSymbol = ticker.startsWith('X:')
    ? `COINBASE:${ticker.replace('X:', '').replace('USD', '')}USD`
    : ticker

  const tvInterval = TV_INTERVAL[interval] ?? 'D'

  useEffect(() => {
    if (!containerRef.current || !ticker) return

    // Clear previous widget
    containerRef.current.innerHTML = ''

    const script = document.createElement('script')
    script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js'
    script.type = 'text/javascript'
    script.async = true
    script.innerHTML = JSON.stringify({
      autosize: false,
      width: '100%',
      height: height,
      symbol: tvSymbol,
      interval: tvInterval,
      timezone: 'America/New_York',
      theme: 'dark',
      style: '1',
      locale: 'en',
      backgroundColor: 'rgba(13, 13, 13, 1)',
      gridColor: 'rgba(42, 42, 42, 0.3)',
      hide_top_toolbar: false,
      hide_legend: false,
      allow_symbol_change: true,
      save_image: false,
      calendar: false,
      hide_volume: false,
      support_host: 'https://www.tradingview.com',
      studies: [],
      toolbar_bg: 'rgba(10, 10, 10, 1)',
      withdateranges: true,
    })

    containerRef.current.appendChild(script)

    return () => {
      if (containerRef.current) containerRef.current.innerHTML = ''
    }
  }, [tvSymbol, tvInterval])

  const hasLevels = entry || stopLoss || target1 || target2
  const dir = direction?.toUpperCase()

  const fmt = (p: number) => p >= 1000
    ? `$${p.toLocaleString('en-US', { maximumFractionDigits: 0 })}`
    : `$${p.toFixed(2)}`

  // R:R calculation
  let rrText = ''
  if (entry && stopLoss && target1) {
    const risk   = Math.abs(entry - stopLoss)
    const reward = Math.abs(target1 - entry)
    if (risk > 0) rrText = `R:R 1:${(reward / risk).toFixed(1)}`
  }

  return (
    <div style={{ borderRadius: 4, overflow: 'hidden', border: '1px solid var(--edge)', background: '#0d0d0d' }}>
      {/* TradingView widget container */}
      <div
        ref={containerRef}
        className="tradingview-widget-container"
        style={{ height, width: '100%' }}
      />

      {/* AI levels overlay bar — compact row below chart */}
      {hasLevels && (
        <div style={{
          display: 'flex', gap: 6, padding: '8px 12px', flexWrap: 'wrap', alignItems: 'center',
          borderTop: '1px solid var(--edge)', background: '#0a0a0a',
        }}>
          <span style={{
            fontFamily: 'Syne', fontWeight: 800, fontSize: '.6rem',
            color: dir === 'LONG' ? 'var(--cash)' : dir === 'SHORT' ? 'var(--red)' : 'var(--amber)',
            padding: '2px 8px', borderRadius: 2,
            border: `1px solid ${dir === 'LONG' ? 'var(--cash-dim)' : dir === 'SHORT' ? 'rgba(224,92,92,.3)' : 'rgba(212,168,67,.3)'}`,
          }}>
            {dir === 'LONG' ? '▲ LONG' : dir === 'SHORT' ? '▼ SHORT' : '— WAIT'}
          </span>

          {entry && (
            <span style={{ fontFamily: 'Syne Mono', fontSize: '.6rem', color: 'var(--amber)', padding: '2px 8px', border: '1px solid rgba(212,168,67,.2)', borderRadius: 2 }}>
              ENTRY {fmt(entry)}
            </span>
          )}
          {stopLoss && (
            <span style={{ fontFamily: 'Syne Mono', fontSize: '.6rem', color: 'var(--red)', padding: '2px 8px', border: '1px solid rgba(224,92,92,.2)', borderRadius: 2 }}>
              SL {fmt(stopLoss)}
            </span>
          )}
          {target1 && (
            <span style={{ fontFamily: 'Syne Mono', fontSize: '.6rem', color: 'var(--cash)', padding: '2px 8px', border: '1px solid var(--cash-dim)', borderRadius: 2 }}>
              T1 {fmt(target1)}
            </span>
          )}
          {target2 && (
            <span style={{ fontFamily: 'Syne Mono', fontSize: '.6rem', color: '#a8d8a0', padding: '2px 8px', border: '1px solid rgba(168,216,160,.2)', borderRadius: 2 }}>
              T2 {fmt(target2)}
            </span>
          )}
          {rrText && (
            <span style={{ fontFamily: 'Syne Mono', fontSize: '.58rem', color: 'var(--cash)', marginLeft: 'auto', padding: '2px 8px', border: '1px solid var(--cash-dim)', borderRadius: 2, background: 'var(--stamp)' }}>
              {rrText}
            </span>
          )}
        </div>
      )}
    </div>
  )
}

export default memo(TradingViewChart)
